import { Injectable } from '@angular/core';
import { Observable, catchError, throwError } from 'rxjs';
import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
  HttpParams,
} from '@angular/common/http';

import { getSsoRoutes } from './server-routes';

import {
  IApiResponse,
  IAddTrustedDevicePayload,
  IAuthorizeSessionData,
  IAuthorizeSessionPayload,
  ICheckDeviceData,
  IForgotPasswordRequestPayload,
  IForgotPasswordVerifyPayload,
  ILoginData,
  ILoginPayload,
  IRecoverySetupPayload,
  IRecoverySetupVerifyPayload,
  IReviewApprovalPayload,
  ITrustedDeviceSolutionPayload,
  IVerifyData,
} from '../../interfaces/auth.interface';

@Injectable({
  providedIn: 'root',
})
export class SsoAuthService {
  private static readonly LOGGED_IN_FLAG = 'sso_logged_in';
  private static readonly COUNTRY_KEY = 'sso_country';

  constructor(private http: HttpClient) {}

  private readonly jsonHeaders = new HttpHeaders({
    'Content-Type': 'application/json',
  });

  private readonly creds = { withCredentials: true as const };

  getSelectedCountry(): string {
    const currentTabCountry = sessionStorage.getItem(SsoAuthService.COUNTRY_KEY);
    const signedInCountry = localStorage.getItem(SsoAuthService.COUNTRY_KEY);

    return (this.hasSessionFlag()
      ? signedInCountry || currentTabCountry
      : currentTabCountry || signedInCountry) || 'INDIA';
  }

  private get routes() {
    return getSsoRoutes(this.getSelectedCountry());
  }

  getStateToken(): string {
    return sessionStorage.getItem('state_token') || '';
  }

  setStateToken(token: string | undefined | null): void {
    if (token) {
      sessionStorage.setItem('state_token', token);
    }
  }

  clearStateToken(): void {
    sessionStorage.removeItem('state_token');
  }

  hasSessionFlag(): boolean {
    return localStorage.getItem(SsoAuthService.LOGGED_IN_FLAG) === '1';
  }

  markSessionActive(): void {
    localStorage.setItem(SsoAuthService.COUNTRY_KEY, this.getSelectedCountry());
    localStorage.setItem(SsoAuthService.LOGGED_IN_FLAG, '1');
  }

  clearSessionFlag(): void {
    localStorage.removeItem(SsoAuthService.LOGGED_IN_FLAG);
    localStorage.removeItem(SsoAuthService.COUNTRY_KEY);
  }

  clearSelectedCountry(): void {
    sessionStorage.removeItem(SsoAuthService.COUNTRY_KEY);
    localStorage.removeItem(SsoAuthService.COUNTRY_KEY);
  }

  getTrustedDeviceId(): string {
    return localStorage.getItem('sso_trusted_device_id') || '';
  }

  setTrustedDeviceId(deviceId: string | undefined | null): void {
    if (deviceId) {
      localStorage.setItem('sso_trusted_device_id', deviceId);
    }
  }

  clearTrustedDeviceId(): void {
    localStorage.removeItem('sso_trusted_device_id');
  }

  private authHeaders(): HttpHeaders {
    const token = this.getStateToken();

    let headers = this.jsonHeaders;

    if (token) {
      headers = headers.set('x-state-token', token);
    }

    return headers;
  }

  getCountryByLocation(
    lat: number,
    long: number,
  ): Observable<IApiResponse<{ country: string }>> {
    const params = new HttpParams().set('lat', lat).set('long', long);

    return this.http
      .get<IApiResponse<{ country: string }>>(
        getSsoRoutes().GET_COUNTRY_BY_LOCATION,
        { params, ...this.creds },
      )
      .pipe(catchError(this.handleError.bind(this)));
  }

  login(payload: ILoginPayload): Observable<IApiResponse<ILoginData>> {
    return this.http
      .post<IApiResponse<ILoginData>>(this.routes.LOGIN, payload, {
        headers: this.jsonHeaders,
        ...this.creds,
      })
      .pipe(catchError(this.handleError.bind(this)));
  }

  sendForgotPasswordOtp(
    payload: IForgotPasswordRequestPayload,
  ): Observable<IApiResponse<{ stateToken?: string }>> {
    return this.http
      .post<IApiResponse<{ stateToken?: string }>>(
        this.routes.FORGOT_PASSWORD_OTP,
        payload,
        { headers: this.jsonHeaders, ...this.creds },
      )
      .pipe(catchError(this.handleError.bind(this)));
  }

  verifyOtp(payload: { otp: string }): Observable<IApiResponse<IVerifyData>> {
    return this.http
      .post<IApiResponse<IVerifyData>>(this.routes.VERIFY_OTP, payload, {
        headers: this.authHeaders(),
        ...this.creds,
      })
      .pipe(catchError(this.handleError.bind(this)));
  }

  verifyForgotPasswordOtp(
    payload: IForgotPasswordVerifyPayload,
  ): Observable<IApiResponse<IVerifyData>> {
    return this.http
      .post<IApiResponse<IVerifyData>>(this.routes.VERIFY_OTP, payload, {
        headers: this.authHeaders(),
        ...this.creds,
      })
      .pipe(catchError(this.handleError.bind(this)));
  }

  verifyPassword(
    payload: { password: string },
  ): Observable<IApiResponse<IVerifyData>> {
    return this.http
      .post<IApiResponse<IVerifyData>>(this.routes.VERIFY_PASSWORD, payload, {
        headers: this.authHeaders(),
        ...this.creds,
      })
      .pipe(catchError(this.handleError.bind(this)));
  }

  recoverySetup(
    payload: IRecoverySetupPayload,
  ): Observable<IApiResponse<ILoginData>> {
    return this.http
      .post<IApiResponse<ILoginData>>(this.routes.RECOVERY_SETUP, payload, {
        headers: this.authHeaders(),
        ...this.creds,
      })
      .pipe(catchError(this.handleError.bind(this)));
  }

  recoverySetupVerify(
    payload: IRecoverySetupVerifyPayload,
  ): Observable<IApiResponse<IVerifyData>> {
    return this.http
      .post<IApiResponse<IVerifyData>>(
        this.routes.RECOVERY_SETUP_VERIFY,
        payload,
        { headers: this.authHeaders(), ...this.creds },
      )
      .pipe(catchError(this.handleError.bind(this)));
  }

  completeRecoverySetup(): Observable<IApiResponse<IVerifyData>> {
    return this.http
      .post<IApiResponse<IVerifyData>>(
        this.routes.COMPLETE_RECOVERY_SETUP,
        {},
        { headers: this.authHeaders(), ...this.creds },
      )
      .pipe(catchError(this.handleError.bind(this)));
  }

  continueUntrusted(): Observable<IApiResponse<IVerifyData>> {
    return this.http
      .post<IApiResponse<IVerifyData>>(
        this.routes.CONTINUE_UNTRUSTED,
        {},
        { headers: this.authHeaders(), ...this.creds },
      )
      .pipe(catchError(this.handleError.bind(this)));
  }

  addTrustedDevice(
    payload: IAddTrustedDevicePayload,
  ): Observable<IApiResponse<IVerifyData>> {
    return this.http
      .post<IApiResponse<IVerifyData>>(
        this.routes.ADD_TRUSTED_DEVICE,
        payload,
        { headers: this.authHeaders(), ...this.creds },
      )
      .pipe(catchError(this.handleError.bind(this)));
  }

  reviewApproval(
    payload: IReviewApprovalPayload,
  ): Observable<IApiResponse<IVerifyData>> {
    return this.http
      .post<IApiResponse<IVerifyData>>(this.routes.REVIEW_APPROVAL, payload, {
        headers: this.authHeaders(),
        ...this.creds,
      })
      .pipe(catchError(this.handleError.bind(this)));
  }

  checkDevice(deviceId: string): Observable<IApiResponse<ICheckDeviceData>> {
    const params = new HttpParams().set('deviceId', deviceId);

    return this.http
      .get<IApiResponse<ICheckDeviceData>>(this.routes.CHECK_DEVICE, {
        params,
        ...this.creds,
      })
      .pipe(catchError(this.handleError.bind(this)));
  }

  loginByTrustedDevice(
    payload: ITrustedDeviceSolutionPayload,
  ): Observable<IApiResponse<IVerifyData>> {
    return this.http
      .post<IApiResponse<IVerifyData>>(
        this.routes.LOGIN_BY_TRUSTED_DEVICE,
        payload,
        { headers: this.jsonHeaders, ...this.creds },
      )
      .pipe(catchError(this.handleError.bind(this)));
  }

  revokeDevice(
    payload: ITrustedDeviceSolutionPayload,
  ): Observable<IApiResponse<any>> {
    return this.http
      .post<IApiResponse<any>>(this.routes.REVOKE_DEVICE, payload, {
        headers: this.jsonHeaders,
        ...this.creds,
      })
      .pipe(catchError(this.handleError.bind(this)));
  }

  authorizeSession(
    payload: IAuthorizeSessionPayload,
  ): Observable<IApiResponse<IAuthorizeSessionData>> {
    return this.http
      .post<IApiResponse<IAuthorizeSessionData>>(
        this.routes.AUTHORIZE_SESSION,
        payload,
        { headers: this.jsonHeaders, ...this.creds },
      )
      .pipe(catchError(this.handleError.bind(this)));
  }

  getProfileDetails(): Observable<IApiResponse<any>> {
    return this.http
      .get<IApiResponse<any>>(this.routes.GET_PROFILE_DETAILS, {
        headers: this.jsonHeaders,
        ...this.creds,
      })
      .pipe(catchError(this.handleError.bind(this)));
  }

  logout(): Observable<IApiResponse<any>> {
    return this.http
      .post<IApiResponse<any>>(
        this.routes.LOGOUT,
        {},
        { headers: this.jsonHeaders, ...this.creds },
      )
      .pipe(catchError(this.handleError.bind(this)));
  }

  private handleError(error: HttpErrorResponse) {
    const errorMessage =
      error?.error?.message ||
      error?.message ||
      'Something went wrong. Please try again.';

    return throwError(() => ({
      message: errorMessage,
      statusCode: error.status,
      error,
    }));
  }
}
